import { isGoogleEmulatorKind } from "./google-emulator.js";
import { isSharedMailboxInstance, isSharedMailboxOwner } from "./shared-mailbox.js";
import { gmailConnectorConfigSchemas, githubConnectorConfigSchemas, googleCalendarConnectorConfigSchemas, googleDriveConnectorConfigSchemas, googleSheetsConnectorConfigSchemas, granolaConnectorConfigSchemas, outlookMailConnectorConfigSchemas, slackConnectorConfigSchemas, } from "./connector-config-schemas-collaboration.js";
import { goCardlessConnectorConfigSchemas, hubSpotConnectorConfigSchemas, overtureConnectorConfigSchemas, sharePointConnectorConfigSchemas, stripeConnectorConfigSchemas, sunConnectorConfigSchemas, twinConnectorConfigSchemas, xeroConnectorConfigSchemas, } from "./connector-config-schemas-enterprise.js";
function ingestionConfig(schemas) {
    return { plane: "ingestion", ...schemas };
}
/**
 * Exhaustive registry for the connector-runner ingestion plane. Runtime plugin
 * routing and write/action policy deliberately live in runtime-plugin contracts.
 */
export const connectorIngestionConfigRegistry = {
    xero: ingestionConfig(xeroConnectorConfigSchemas),
    sun: ingestionConfig(sunConnectorConfigSchemas),
    overture: ingestionConfig(overtureConnectorConfigSchemas),
    stripe: ingestionConfig(stripeConnectorConfigSchemas),
    gocardless: ingestionConfig(goCardlessConnectorConfigSchemas),
    sharepoint: ingestionConfig(sharePointConnectorConfigSchemas),
    gmail: ingestionConfig(gmailConnectorConfigSchemas),
    outlook_mail: ingestionConfig(outlookMailConnectorConfigSchemas),
    google_calendar: ingestionConfig(googleCalendarConnectorConfigSchemas),
    google_drive: ingestionConfig(googleDriveConnectorConfigSchemas),
    google_sheets: ingestionConfig(googleSheetsConnectorConfigSchemas),
    github: ingestionConfig(githubConnectorConfigSchemas),
    granola: ingestionConfig(granolaConnectorConfigSchemas),
    slack: ingestionConfig(slackConnectorConfigSchemas),
    hubspot: ingestionConfig(hubSpotConnectorConfigSchemas),
    twin: ingestionConfig(twinConnectorConfigSchemas),
};
export function connectorConfigSchemaFor(provider, operation) {
    return connectorIngestionConfigRegistry[provider][operation];
}
export function safeParseConnectorConfig(provider, operation, config) {
    return connectorConfigSchemaFor(provider, operation).safeParse(config);
}
export function parseConnectorConfig(provider, operation, config) {
    const parsed = connectorConfigSchemaFor(provider, operation).parse(config);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error(`${provider} ${operation} connector config schema returned a non-object`);
    }
    return parsed;
}
export function addConnectorConfigValidationIssues(input) {
    const parsed = safeParseConnectorConfig(input.provider, input.operation, input.config);
    if (parsed.success)
        return;
    const prefix = typeof input.path === "object"
        ? input.path
        : [input.path];
    for (const issue of parsed.error.issues) {
        input.ctx.addIssue({
            code: "custom",
            path: [...prefix, ...issue.path],
            message: `${input.provider} ingestion config: ${issue.message}`,
        });
    }
}
/**
 * Persisted legacy rows are diagnosed, never coerced or silently defaulted.
 * Callers can stop the job and migrate the row using the returned safe issues.
 */
export function validatePersistedConnectorConfig(provider, config) {
    const parsed = safeParseConnectorConfig(provider, "persisted", config);
    if (parsed.success) {
        return {
            ok: true,
            config: parsed.data,
        };
    }
    return {
        ok: false,
        code: "connector_config_migration_required",
        provider,
        issues: parsed.error.issues.map((issue) => {
            const path = issue.path.length > 0 ? issue.path.join(".") : "config";
            return `${path}: ${issue.message}`;
        }),
    };
}
const ALWAYS_USER_SCOPED_PROVIDERS = new Set([
    "gmail",
    "outlook_mail",
    "google_calendar",
    "google_drive",
    "google_sheets",
    "granola",
    "slack",
]);
const GITHUB_REPOSITORY_ENTITIES = new Set([
    "repository",
    "branch",
    "commit",
    "pull_request",
]);
/**
 * Validates configuration together with the authoritative integration row.
 * Cross-field lifecycle, user-scope, and entity-family invariants cannot be
 * represented safely by a config-only provider schema.
 */
export function validatePersistedConnectorIntegrationConfig(input) {
    const configValidation = validatePersistedConnectorConfig(input.provider, input.config);
    if (!configValidation.ok)
        return configValidation;
    const config = configValidation.config;
    const issues = [];
    const addIssue = (path, message) => {
        issues.push(`${path}: ${message}`);
    };
    const githubKind = input.provider === "github"
        ? config.connectorKind
        : undefined;
    const userScoped = !isGoogleEmulatorKind(input.provider, config.connectorKind) && (ALWAYS_USER_SCOPED_PROVIDERS.has(input.provider)
        || (input.provider === "github" && githubKind === "notifications"));
    if (isSharedMailboxInstance(input.provider, config.connectorKind) !== isSharedMailboxOwner(input.userId)) {
        addIssue("userId", "a shared mailbox, and only a shared mailbox, has a mailbox owner");
    }
    if (userScoped) {
        if (!input.userId.trim()) {
            addIssue("userId", "a user-scoped connector requires an authoritative row user");
        }
        if (config.userContextId !== input.userId) {
            addIssue("config.userContextId", "must match the authoritative row user");
        }
    }
    else if (input.userId) {
        addIssue("userId", "an organisation-scoped connector cannot have a row user");
    }
    if (input.provider === "github") {
        if (githubKind === "notifications") {
            if (input.enabledEntities.length === 0
                || input.enabledEntities.some((entity) => entity !== "notification")) {
                addIssue("enabledEntities", "a GitHub notification connector must enable only notification");
            }
        }
        else if (input.enabledEntities.some((entity) => !GITHUB_REPOSITORY_ENTITIES.has(entity))) {
            addIssue("enabledEntities", "a GitHub repository connector contains a non-repository entity");
        }
    }
    if (input.provider === "gocardless") {
        const accountEntities = new Set([
            "accounts",
            "account_details",
            "balances",
            "transactions",
        ]);
        const enablesEveryEntity = input.enabledEntities.length === 0;
        if (input.enabled
            &&
                (enablesEveryEntity
                    || input.enabledEntities.some((entity) => accountEntities.has(entity)))
            && (!Array.isArray(config.accountIds) || config.accountIds.length === 0)) {
            addIssue("config.accountIds", "is required by the enabled account entities");
        }
        if (input.enabled
            &&
                (enablesEveryEntity || input.enabledEntities.includes("requisitions"))
            && typeof config.requisitionId !== "string") {
            addIssue("config.requisitionId", "is required by the enabled requisitions entity");
        }
    }
    const disabledLifecycle = !input.enabled && input.status === "disabled";
    if (input.provider === "stripe"
        && config.disconnectedAt !== undefined
        && !disabledLifecycle) {
        addIssue("config.disconnectedAt", "is valid only on a disabled integration");
    }
    if ((input.provider === "sharepoint" || input.provider === "outlook_mail")
        && config.disconnectCleanupRequestedAt !== undefined
        && !disabledLifecycle) {
        addIssue("config.disconnectCleanupRequestedAt", "is valid only on a disabled integration");
    }
    if (input.provider === "granola"
        && config.authMode === "retired"
        && !disabledLifecycle) {
        addIssue("config.authMode", "retired Granola config is valid only when disabled");
    }
    return issues.length === 0
        ? { ok: true, config }
        : {
            ok: false,
            code: "connector_config_migration_required",
            provider: input.provider,
            issues,
        };
}
